import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  TRACKER_MAX_EXERCISES,
  TRACKER_RANGE_DAYS,
  TRACKER_RANGES,
  TRACKER_FREE_RANGE,
  TRACKER_FREE_STATS,
  TRACKER_STATS,
  formatAxisDay,
  trackableSlugs,
  trackerTrack,
  trailingRange,
  type StoredExerciseTracker,
  type TrackerRange,
  type TrackerStat,
} from "@heraion/shared";

import { LineSwatch } from "../charts/LineSwatch";
import type { TFn } from "../i18nTypes";
import { joinList } from "../joinList";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { machineLabel } from "../Logger/machineLabel";
import { Button } from "../primitives/Button";
import { useDateLocale } from "../primitives/DateFormatContext";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { PremiumBadge } from "../primitives/PremiumBadge";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { ChipMultiRow } from "../primitives/ChipMultiRow";
import type { UiTheme } from "../theme";
import { weightUnitLabels } from "../weightUnitLabels";
import { ExerciseTrackerChart } from "./ExerciseTrackerChart";
import {
  lineStyleLabel,
  rangeA11y,
  rangeLabel,
  TRACKER_STAT_STROKE,
  trackerFormat,
  trackerStatLabel,
} from "./exerciseTrackerModel";
import { PanelTitleRow } from "./InfoPill";
import { NoDataCard, StaleHint } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { nowToTheMinute } from "./progressWindow";
import type { ProgressDeps } from "./types";
import { useExerciseTracker, type SaveExerciseTracker } from "./useExerciseTracker";
import { useRangeResultsCache } from "./useRangeResultsCache";
import { staleCovering } from "./staleCovering";
import { useSessionResultsFromDeps } from "./useSessionResults";

// #389: the Progress widget that follows the exercises the athlete picks.
// He picks the exercises (up to TRACKER_MAX_EXERCISES, through the host's
// picker, one at a time), the statistics and the range; the choice is saved
// on his profile. ONE key for the statistics on top, then one chart per
// exercise. The one widget whose window he chooses (progressWindow.ts says
// the others are fixed, PG-64): he asked for it. It reads that whole range,
// page by page (the report's way), never the newest 200 rows: 12 months can
// hold more, and a missing start would move every curve's 0%.
// #392: a free account sees the free tier (clampToTier); the other chips are
// locked under the Premium badge and the add button is gone.

export function ExerciseTrackerPanel({
  stored,
  onSave,
  isPremium = false,
  ...deps
}: ProgressDeps & {
  /** Profile.exerciseTracker; absent = the defaults. */
  stored?: StoredExerciseTracker;
  /** Absent: the choice holds for the visit only. */
  onSave?: SaveExerciseTracker;
  /** The host's isPremium(profile). Absent: free. */
  isPremium?: boolean;
}) {
  const { theme, units } = deps;
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const card = useMemo(() => makeTierOneStyles(theme), [theme]);
  const tracker = useExerciseTracker(stored, onSave, isPremium);

  const nowMs = nowToTheMinute();
  const range = useMemo(
    () => trailingRange(nowMs, TRACKER_RANGE_DAYS[tracker.range]),
    [nowMs, tracker.range],
  );
  const rangeCache = useRangeResultsCache(deps.resultsCache, tracker.range);
  // The report's recipe (ProgressReportSheet): the range and a cache of its
  // own. Progress's disk copy (its newest rows) only where it holds the whole
  // range (#397): a missing start would move every 0%.
  const readStale = useMemo(() => staleCovering(deps.readStaleResults, range), [deps.readStaleResults, range]);
  const { results, catalog, error, current } = useSessionResultsFromDeps({
    ...deps,
    resultsRange: range,
    resultsCache: rangeCache,
    readStaleResults: readStale,
    persistResults: undefined,
  });
  const getName = useExerciseNameBySlug(catalog);
  // A range picked for the first time: the rows of the one before stay
  // under the chips, but a chart drawn from them would start late and move
  // every 0%, so the charts wait for this range's rows.
  const rangeLoaded = current;
  const trackable = useMemo(() => (results ? trackableSlugs(results, range) : []), [results, range]);
  const fallback = trackable[0] ?? null;
  const { choiceWith } = tracker;
  const choice = useMemo(() => choiceWith(fallback), [choiceWith, fallback]);
  const update = (op: Parameters<typeof tracker.update>[0]) => tracker.update(op, fallback);
  const [picking, setPicking] = useState(false);

  const trackBySlug = useMemo(
    () => new Map(choice.exercises.map((slug) => [slug, results ? trackerTrack(results, slug, range) : undefined])),
    [choice.exercises, results, range],
  );
  const dateLocale = useDateLocale();
  const dateLabel = useCallback((atMs: number) => formatAxisDay(new Date(atMs), dateLocale), [dateLocale]);
  const format = useMemo(
    () => trackerFormat(t, units, i18n.language, weightUnitLabels(t)),
    [t, units, i18n.language],
  );
  // A new range, statistic or exercise mounts each chart afresh: the point
  // tapped before belongs to data no longer drawn.
  const remountKey = [choice.range, ...choice.stats, ...choice.exercises].join("|");

  const toggleStat = (stat: TrackerStat) =>
    update((c) => ({
      ...c,
      stats: TRACKER_STATS.filter((s) => (s === stat ? !c.stats.includes(s) : c.stats.includes(s))),
    }));
  const pickRange = (r: TrackerRange) => update((c) => ({ ...c, range: r }));
  const addExercise = (ex: { slug: string }) => {
    setPicking(false);
    update((c) =>
      c.exercises.includes(ex.slug) || c.exercises.length >= TRACKER_MAX_EXERCISES
        ? c
        : { ...c, exercises: [...c.exercises, ex.slug] },
    );
  };
  const removeExercise = (slug: string) =>
    update((c) => ({ ...c, exercises: c.exercises.filter((s) => s !== slug) }));

  if (results === null) return <NoDataCard theme={theme} titleKey="tracker" error={error || null} />;

  const Picker = deps.ExercisePickerMount;
  // Free: every chip is fixed; the ones outside the tier also say Premium.
  const locked = () => !isPremium;
  const lockedA11y = (name: string, free: boolean) =>
    isPremium || free ? name : t("mobile.premium.lockedItemA11y", { name, defaultValue: "{{name}}, Premium" });
  const eligible = trackable.filter((s) => !choice.exercises.includes(s));
  const full = choice.exercises.length >= TRACKER_MAX_EXERCISES;

  return (
    <View style={card.card}>
      <PanelTitleRow
        theme={theme}
        title={t("mobile.progress.tracker.title", { defaultValue: "Exercise tracker" })}
        body={t("mobile.progress.tracker.help", {
          max: TRACKER_MAX_EXERCISES,
          defaultValue:
            "Follow up to {{max}} exercises. Each chart draws the statistics you pick as the change since the first session in the range, so kilos and reps share one axis. Tap a point to read the real values of that session.",
        })}
        caption={t("progress.chartTapHint", { defaultValue: "Tap a point to read its value" })}
      />
      {error ? <StaleHint theme={theme} /> : null}
      <ChipChoiceRow
        theme={theme}
        choices={TRACKER_RANGES}
        selected={choice.range}
        onSelect={pickRange}
        label={(r) => rangeLabel(t, r)}
        chipA11yLabel={(r) => lockedA11y(rangeA11y(t, r), r === TRACKER_FREE_RANGE)}
        isDisabled={locked}
        groupA11yLabel={t("mobile.progress.tracker.rangeTitle", { defaultValue: "Range" })}
      />
      {isPremium ? null : (
        <View style={styles.premium}>
          <View {...hiddenFromScreenReader()}>
            <PremiumBadge theme={theme} />
          </View>
          <Text style={[styles.note, styles.premiumText]}>
            {t("mobile.progress.tracker.premiumHint", {
              defaultValue: "Other ranges, statistics and more exercises are Premium.",
            })}
          </Text>
        </View>
      )}
      <ChipMultiRow
        theme={theme}
        choices={TRACKER_STATS}
        selected={new Set(choice.stats)}
        onToggle={toggleStat}
        label={(s) => trackerStatLabel(t, s)}
        chipA11yLabel={(s) => lockedA11y(trackerStatLabel(t, s), TRACKER_FREE_STATS.includes(s))}
        isDisabled={locked}
        groupA11yLabel={t("mobile.progress.tracker.statsTitle", { defaultValue: "Statistics" })}
      />
      {choice.stats.length === 0 ? (
        <Text style={card.empty}>
          {t("mobile.progress.tracker.noStats", { defaultValue: "Choose at least one statistic." })}
        </Text>
      ) : (
        <TrackerKey theme={theme} t={t} locale={i18n.language} stats={choice.stats} />
      )}
      {!rangeLoaded ? (
        <ActivityIndicator color={theme.palette.brand500} />
      ) : choice.exercises.length === 0 ? (
        <Text style={card.empty}>
          {t("mobile.progress.tracker.nothing", {
            defaultValue: "Log an exercise with a load to follow it here.",
          })}
        </Text>
      ) : choice.stats.length === 0 ? null : (
        choice.exercises.map((slug) => {
          const name = getName(slug);
          const track = trackBySlug.get(slug);
          const sessions = track?.sessions ?? [];
          return (
            <ExerciseTrackerChart
              key={`${slug}|${remountKey}`}
              theme={theme}
              name={track && track.trackCount > 1 ? onMachine(t, name, track.equipmentInstance) : name}
              sessions={sessions}
              stats={choice.stats}
              format={format}
              dateLabel={dateLabel}
              nowMs={nowMs}
              emptyText={
                sessions.length === 0
                  ? t("mobile.progress.tracker.noData", { defaultValue: "No sessions in this range." })
                  : t("mobile.progress.tracker.noStatData", {
                      defaultValue: "None of the chosen statistics is recorded for this exercise in this range.",
                    })
              }
              remove={
                choice.exercises.length > 1
                  ? {
                      a11yLabel: t("mobile.progress.tracker.remove", {
                        name,
                        defaultValue: "Stop following {{name}}",
                      }),
                      onPress: () => removeExercise(slug),
                    }
                  : undefined
              }
            />
          );
        })
      )}
      {Picker && isPremium && full ? (
        <Text style={styles.note}>
          {t("mobile.progress.tracker.full", {
            max: TRACKER_MAX_EXERCISES,
            defaultValue: "Up to {{max}} exercises.",
          })}
        </Text>
      ) : null}
      {Picker && isPremium && !full && eligible.length > 0 ? (
        <Button
          theme={theme}
          variant="secondary"
          icon="plus"
          label={t("mobile.progress.tracker.add", { defaultValue: "Add an exercise" })}
          onPress={() => setPicking(true)}
          style={styles.add}
        />
      ) : null}
      {tracker.saveFailed ? (
        <Text style={card.errorText}>
          {t("mobile.progress.tracker.saveError", {
            defaultValue: "Couldn't save your choice. Try again.",
          })}
        </Text>
      ) : null}
      {Picker ? (
        <Picker
          visible={picking}
          onClose={() => setPicking(false)}
          onPickSingle={addExercise}
          eligibleSlugs={eligible}
        />
      ) : null}
    </View>
  );
}

/** The one key: each chosen statistic's line and name, captioned with what
 *  the y axis measures. One label to a screen reader, naming each line by
 *  its dash, since the swatches are not seen. */
function TrackerKey({
  theme,
  t,
  locale,
  stats,
}: {
  theme: UiTheme;
  t: TFn;
  locale: string;
  stats: readonly TrackerStat[];
}) {
  const styles = makeStyles(theme);
  const caption = t("mobile.progress.tracker.axis", {
    defaultValue: "Each curve shows the change since its first session in the range.",
  });
  const entries = stats.map((stat) =>
    t("mobile.progress.tracker.lineEntry", {
      line: lineStyleLabel(t, TRACKER_STAT_STROKE[stat]),
      stat: trackerStatLabel(t, stat),
      defaultValue: "{{line}}: {{stat}}",
    }),
  );
  return (
    // "image", as CoverageRowShell: RNW gives the default no web role, and a
    // node whose children are all hidden could be skipped (PG-53).
    <View
      style={styles.key}
      accessible
      accessibilityRole="image"
      accessibilityLabel={`${joinList(entries, locale)}. ${caption}`}
    >
      <View style={styles.entries} {...hiddenFromScreenReader()}>
        {stats.map((stat) => (
          <View key={stat} style={styles.entry}>
            <LineSwatch theme={theme} stroke={TRACKER_STAT_STROKE[stat]} />
            <Text style={styles.name}>{trackerStatLabel(t, stat)}</Text>
          </View>
        ))}
      </View>
      <Text style={styles.note} {...hiddenFromScreenReader()}>
        {caption}
      </Text>
    </View>
  );
}

/** The chart's title when the exercise has more than one machine in range:
 *  the machine it reads, named as the e1RM panel names it. */
function onMachine(t: TFn, exercise: string, instance: string | undefined): string {
  const machine =
    instance === undefined
      ? t("swap.defaultMachine", { defaultValue: "Default machine" })
      : machineLabel(instance, t);
  return t("mobile.progress.tracker.onMachine", {
    exercise,
    machine,
    defaultValue: "{{exercise}} · {{machine}}",
  });
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    key: { gap: s.s1 },
    entries: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", columnGap: s.s3, rowGap: s.s1 },
    entry: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    name: { color: p.ink2, fontSize: ty.sizes.sm },
    note: { color: p.ink3, fontSize: ty.sizes.sm },
    add: { alignSelf: "flex-start" },
    premium: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: s.s2 },
    premiumText: { flexShrink: 1, minWidth: 0 },
  });
}
